import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';

import { GetSubsetInconsistenciesResponse } from '../../../../backend/src/matter/inconsistency/dto/get-subset-inconsistencies.dto';
import { InconsistencyLevel } from '../../../../backend/src/matter/inconsistency/inconsistency-display';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

export const useUpdateInconsistency = ({ matterId, subsetId }: { matterId: string; subsetId: string }) => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ inconsistencyId, level }: { inconsistencyId: string; level: InconsistencyLevel }) => {
      const fetchConfig = getFetchConfig({
        method: FetchMethod.PATCH,
        data: { level },
      });

      const response = await fetch(
        `${APIBaseChronos}/api/matter/${matterId}/inconsistency/${inconsistencyId}`,
        fetchConfig,
      );

      if (response.status === 403) {
        toast.error('You are not allowed to edit this inconsistency.');
        throw new Error('Failed to update inconsistency');
      }

      if (!response.ok) {
        toast.error('Failed to update inconsistency. Please try again.');
        throw new Error('Failed to update inconsistency');
      }

      trackCustomEvent(TrackingEventsV2.InconsistencyEdit, { matterId, inconsistencyId, level });
    },
    {
      onMutate: async ({ inconsistencyId, level }) => {
        await queryClient.cancelQueries({ queryKey: [QueryEntity.SubsetInconsistencies, { subsetId }] });

        const previousData = queryClient.getQueriesData<GetSubsetInconsistenciesResponse>([
          QueryEntity.SubsetInconsistencies,
          { subsetId },
        ]);

        queryClient.setQueriesData<GetSubsetInconsistenciesResponse | undefined>(
          [QueryEntity.SubsetInconsistencies, { subsetId }],
          (old) => {
            if (!old?.inconsistencies) return old;
            return {
              ...old,
              inconsistencies: old.inconsistencies.map((inc) => (inc.id === inconsistencyId ? { ...inc, level } : inc)),
            };
          },
        );

        return { previousData };
      },
      onSuccess: () => {
        toast.success('Inconsistency updated');
      },
      onError: (_error, _variables, context) => {
        if (context?.previousData) {
          for (const [queryKey, data] of context.previousData) {
            queryClient.setQueryData(queryKey, data);
          }
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: [QueryEntity.SubsetInconsistencies, { subsetId }] });
      },
    },
  );
};
